import { EnvironmentSettingsType } from "services/settings/SettingsTypes";
import { createNewResource, ResourceGroupType, SchedulingBoardType } from "../SchedulingTypes";
import { v4 as uuidv4 } from 'uuid';

// Generate active resources
export const generateResources = (
    schedulingBoard: SchedulingBoardType,
    days: Date[],
    selectedResourceGroups: number[],
    environmentSettings: EnvironmentSettingsType
): {
    selectableResourceGroups: ResourceGroupType[],
    currentResourceGroups: ResourceGroupType[]
} => {

    // Return empty array if days or not set
    if (!days.length) return { selectableResourceGroups: [], currentResourceGroups: [] };

    // Initialize range start and end
    const rangeStart = days[0];
    const rangeEnd = days[days.length - 1];

    // Filter the fetched groups on selected
    const filteredGroupsBySelection = schedulingBoard.resource_groups
        .filter((group: ResourceGroupType) => {

            // Return if group is not selectable
            if (group.id === null || group.id === undefined) return false;

            const groupId = Number(group.id);

            return (
                selectedResourceGroups.length === 0 || 
                selectedResourceGroups.includes(groupId)
            );
        });

    // Sort the fetched resource groups
    let sortedFilteredGroups: ResourceGroupType[] =
        filteredGroupsBySelection
            .slice()
            .sort((a: ResourceGroupType, b: ResourceGroupType) => a.ordering - b.ordering)
            .map((group: ResourceGroupType) => {

                // Filter on active resources
                const filteredResources = group.resources.filter((resource) => {

                    // Get the deleted at date
                    const deleted_at = resource.deleted_at
                        ? new Date(resource.deleted_at)
                        : null;

                    // Don't render the resource if the resource is deleted before the start date
                    if (deleted_at && deleted_at < rangeStart) {
                        return false;
                    }

                    // Get the created date of the linked user
                    const userCreated = resource.user_created
                        ? new Date(resource.user_created)
                        : null;

                    // Show the resource from the monday of the week the user was
                    // created, so new trial accounts see themselves in the current week
                    if (userCreated) {
                        const visibleFrom = new Date(userCreated);
                        visibleFrom.setDate(visibleFrom.getDate() - ((visibleFrom.getDay() + 6) % 7));
                        visibleFrom.setHours(0, 0, 0, 0);

                        // Don't render the resource before the monday of the creation week
                        if (visibleFrom > rangeEnd) {
                            return false;
                        }
                    }

                    // Otherwise render the resource
                    return true;
                });

                return {
                    ...group,
                    resources: filteredResources
                };
            })

            // Filter out empty resource groups
            .filter((group: ResourceGroupType) => group.resources.length > 0); 

    // Get the show unassigned row setting from the environment settings
    const { scheduling_show_unassigned_row } = environmentSettings;

    // Add unassigned resource(group)
    if (scheduling_show_unassigned_row) {

        // Return empty array if current scheduling board is not loaded
        if (!schedulingBoard) return { selectableResourceGroups: [], currentResourceGroups: [] };

        // Create the unassigned resource and add it to an unassigned group
        const unassignedResource = createNewResource('unassigned', uuidv4(), 1);

        // Create the unassigned resource group
        const unassignedResourceGroup: ResourceGroupType = {
            id: 'unassigned',
            name: 'Unassigned',
            scheduling_board: schedulingBoard.id,
            ordering: -1,
            deleted: false,
            deleted_at: null,
            division: null,
            resources: [unassignedResource]
        };

        // Add the unassigned resource group before the filtered groups
        return { 
            selectableResourceGroups: schedulingBoard.resource_groups, 
            currentResourceGroups: [unassignedResourceGroup, ...sortedFilteredGroups]
        };
    }

    return {
        selectableResourceGroups: schedulingBoard.resource_groups,
        currentResourceGroups: sortedFilteredGroups
    };
}